import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Terminal, ArrowRight, Trash2, LogOut, Boxes, Sparkles, Globe } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";

const IDEAS = [
  "A kanban board with drag & drop and local persistence",
  "A personal finance tracker with charts",
  "A pomodoro timer with task list and stats",
  "A recipe app with search and favorites",
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [creating, setCreating] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = () =>
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}).finally(() => setLoaded(true));

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!prompt.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/projects", { prompt });
      navigate(`/builder/${data.id}?autobuild=1`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setCreating(false);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.id !== id));
      toast.success("Project deleted");
    } catch {
      toast.error("Could not delete project");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Terminal className="h-5 w-5 text-primary" />
            <span className="font-heading font-bold text-lg tracking-tight">FORGE</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block font-mono text-xs text-muted-foreground">
              {user?.email}
            </span>
            <ThemeToggle />
            <button
              data-testid="logout-button"
              onClick={logout}
              className="h-9 px-3 flex items-center gap-2 border border-border rounded-sm text-sm hover:bg-muted transition-colors"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Prompt hero */}
        <div className="max-w-3xl">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            // new build
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter mt-3">
            What are we building today?
          </h1>
          <p className="text-muted-foreground mt-4 max-w-xl">
            Describe your app in plain English. Forge writes the code, previews it live,
            and gives you a shareable URL.
          </p>

          <div className="mt-8 border border-border rounded-sm bg-card">
            <textarea
              data-testid="new-project-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. Build a habit tracker with streaks, a calendar heatmap and dark mode"
              rows={3}
              className="w-full bg-transparent p-4 resize-none outline-none font-body text-base placeholder:text-muted-foreground"
            />
            <div className="flex items-center justify-between border-t border-border p-3">
              <span className="font-mono text-xs text-muted-foreground flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-primary" /> claude-sonnet-4.6
              </span>
              <button
                data-testid="create-project-button"
                onClick={create}
                disabled={creating || !prompt.trim()}
                className="h-10 px-5 bg-primary text-primary-foreground font-mono text-sm uppercase tracking-wider rounded-sm flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {creating ? "spinning up..." : "generate"}
                {!creating && <ArrowRight className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {IDEAS.map((idea) => (
              <button
                key={idea}
                onClick={() => setPrompt(idea)}
                className="text-xs font-mono border border-border rounded-full px-3 py-1.5 text-muted-foreground hover:border-primary hover:text-foreground transition-colors"
              >
                {idea}
              </button>
            ))}
          </div>
        </div>

        {/* Projects */}
        <div className="mt-16">
          <div className="flex items-center gap-2 mb-6">
            <Boxes className="h-5 w-5" />
            <h2 className="font-heading text-2xl font-bold tracking-tight">Your projects</h2>
            <span className="font-mono text-xs text-muted-foreground">({projects.length})</span>
          </div>

          {!loaded ? (
            <p className="font-mono text-sm text-muted-foreground animate-blink">loading projects...</p>
          ) : projects.length === 0 ? (
            <div className="border border-dashed border-border rounded-sm p-16 text-center">
              <p className="font-heading text-xl font-semibold">No projects yet</p>
              <p className="text-muted-foreground mt-2">
                Your generated apps will appear here. Describe one above to get started.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {projects.map((p) => (
                <div
                  key={p.id}
                  data-testid="project-card"
                  onClick={() => navigate(`/builder/${p.id}`)}
                  className="group cursor-pointer border border-border rounded-sm bg-card p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-[4px_4px_0px_0px_hsl(var(--foreground))]"
                >
                  <div className="flex items-start justify-between">
                    <div className="h-9 w-9 flex items-center justify-center border border-border rounded-sm bg-muted">
                      <Terminal className="h-4 w-4 text-primary" />
                    </div>
                    <button
                      data-testid="delete-project-button"
                      onClick={(e) => remove(p.id, e)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 flex items-center justify-center border border-border rounded-sm hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <h3 className="font-heading text-lg font-semibold mt-4 truncate">{p.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2 h-10">
                    {p.prompt}
                  </p>
                  <div className="flex items-center gap-2 mt-4 font-mono text-xs">
                    {p.published ? (
                      <span className="flex items-center gap-1 text-primary">
                        <Globe className="h-3 w-3" /> published
                      </span>
                    ) : (
                      <span className="text-muted-foreground">draft</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
